import Yup from '@utils/Yupi18n';

import { ID_FOCA } from '@data/constants/Eventos';

const patternINE = /[A-Z|a-z]{6}[0-9]{8}[A-Z|a-z]{1}[0-9]{3}/;
const patternCURP = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z\d]\d$/;

const preguntasSchema = (isEdit = false) =>
  Yup.array()
    .of(
      Yup.object().shape({
        ...(isEdit ? { idPregunta: Yup.number().required() } : {}),
        Pregunta: Yup.string()
          .min(5, 'La pregunta debe tener al menos 5 caracteres')
          .required('La pregunta es requerida'),
        Valor: Yup.number().required(),
        idTipoRespuesta: Yup.number().oneOf([1, 2, 3, 4]).required(),
        Requerido: Yup.boolean().required(),
        Respuestas: Yup.array().when('idTipoRespuesta', {
          is: (val) => val === 1 || val === 2 || val === 4,
          then: Yup.array().when('idTipoRespuesta', {
            is: (val) => val === 1 || val === 2,
            then: Yup.array()
              .min(2, 'La pregunta debe tener al menos 2 respuestas')
              .of(
                Yup.object().shape({
                  ...(isEdit ? { idRespuesta: Yup.number().required() } : {}),
                  Respuesta: Yup.string()
                    .min(2, 'La respuesta debe tener al menos 2 caracteres')
                    .required('La respuesta es requerida'),
                  Correcta: Yup.boolean().required(),
                })
              )
              .test(
                'at-least-one-correct',
                'Al menos una respuesta debe ser correcta',
                (value) => Array.isArray(value) && value.some((respuesta) => respuesta.Correcta)
              )
              .required(),
            otherwise: Yup.array()
              .min(1, 'Debe haber exactamente 1 archivo')
              .max(1, 'Debe haber exactamente 1 archivo')
              .of(
                Yup.object().shape({
                  ...(isEdit ? { idRespuesta: Yup.number().required() } : {}),
                  Respuesta: Yup.object().shape({
                    file: Yup.string().optional().nullable().label('Archivo'),
                    allowTypes: Yup.array().of(Yup.number()).min(1).required().label('Tipos permitidos'),
                    idFile: Yup.string().min(3).max(50).required().label('Identificador de archivo'),
                    ...(isEdit ? { fileURL: Yup.string().optional().nullable() } : {}),
                  }),
                  Correcta: Yup.boolean().required(),
                })
              )
              .test(
                'at-least-one-correct',
                'Al menos una respuesta debe ser correcta',
                (value) => Array.isArray(value) && value.some((respuesta) => respuesta.Correcta)
              )
              .required(),
          }),
          otherwise: Yup.array().notRequired(),
        }),
      })
    )
    .min(1, 'Debe agregar al menos una pregunta')
    .test('no-repetir-idfile', 'No se pueden repetir identificadores de archivo', (preguntas) => {
      if (!Array.isArray(preguntas)) return true;

      const ids = [];
      for (const pregunta of preguntas) {
        if (pregunta.idTipoRespuesta === 4 && Array.isArray(pregunta.Respuestas)) {
          for (const respuesta of pregunta.Respuestas) {
            if (respuesta.Respuesta?.idFile) {
              if (ids.includes(respuesta.Respuesta.idFile)) return false; // Se repitió un idFile
              ids.push(respuesta.Respuesta.idFile);
            }
          }
        }
      }
      return true; // No se repitieron
    })
    .required();

export const EventosSchema = Yup.object({
  imgURL: Yup.string().optional(),
  imgMovilURL: Yup.string().optional(),
  files: Yup.array()
    .min(1, 'Ingresa al menos 1 imagen')
    .required()
    .label('Imágenes del evento')
    .test('fileType', 'Imagen inválida (JPEG, JPG, PNG).', (files) => {
      if (!files) return true;
      return files.every((file) => {
        const validImageTypes = ['image/jpeg', 'image/png'];
        return validImageTypes.includes(file.type);
      });
    })
    .test('fileSize', 'El tamaño de la imagen no debe exceder 5 MB.', (files) => {
      if (!files) return true;
      return files.every((file) => file.size <= 5 * (1024 * 1024));
    }),

  idPartido: Yup.number().required(),
  idAmbito: Yup.number().required(),
  AmbitoValor: Yup.number().required(),

  idTipoActividad: Yup.number().required(),
  Actividad: Yup.string().min(4).required('Nombre del evento es requerido'),
  Descripcion: Yup.string().min(5).required(),
  FechaInicio: Yup.date().required().label('Fecha de inicio del evento'),
  FechaFin: Yup.date()
    .required()
    .label('Fecha de fin del evento')
    .when('FechaInicio', (startDate, schema) => {
      if (startDate instanceof Date) {
        return schema.min(startDate, 'La fecha de fin debe ser posterior a la fecha de inicio');
      }
      return schema;
    }),

  idPlantillaQR: Yup.number().required(),
  idModalidad: Yup.number().required(),
  NombreLugar: Yup.string().min(6).whenCM(Yup.string(), 'idModalidad', [1, 3]).label('Nombre del Lugar'),
  Calle: Yup.string().whenCM(Yup.string().min(2), 'idModalidad', [1, 3]),
  NumExt: Yup.string().whenCM(Yup.string().min(1), 'idModalidad', [1, 3]).label('Num Ext'),
  NumInt: Yup.string().optional().min(0).max(20).nullable().label('Num Int'),
  Colonia: Yup.string().whenCM(Yup.string().min(3), 'idModalidad', [1, 3]),
  idMunicipio: Yup.number().whenCM(Yup.number().positive('Elige un municipio'), 'idModalidad', [1, 3]),
  LigaVirtual: Yup.string()
    .whenCM(
      Yup.string()
        .matches(/^(ftp|http|https):\/\/[^ "]+$/, 'El link no es válido')
        .min(10),
      'idModalidad',
      [2, 3]
    )
    .label('Liga del Evento'),

  esConcurrente: Yup.bool().required(),
  diasConcurrentes: Yup.array().whenCM(
    Yup.array().min(1, 'Debe seleccionar al menos un día'),
    'esConcurrente',
    true
  ),

  tienePreguntas: Yup.bool().required(),
  preguntas: Yup.array().when('tienePreguntas', {
    is: true,
    then: preguntasSchema(false),
    otherwise: Yup.array().optional(),
  }),

  TienePreregistro: Yup.number().required(),
  FechaFinPreregistro: Yup.date()
    .whenCM(
      Yup.date().max(
        Yup.ref('FechaInicio'),
        'La fecha de fin del preregistro no puede ser posterior a la fecha de inicio del evento'
      ),
      ['TienePreregistro', 'esConcurrente'],
      [1, false],
      'and'
    )
    .label('Fecha de fin preregistro del evento'),

  noSesiones: Yup.number().min(1).max(99).required().label('Numero de sesiones'),
  sesiones: Yup.array().when('noSesiones', {
    is: (noSesiones) => noSesiones && noSesiones > 1,
    then: Yup.array()
      .of(
        Yup.date()
          .required('La fecha es obligatoria.')
          .test('fecha-cronologica', 'La fecha debe ser posterior a la anterior.', function (value) {
            const { path, parent } = this;
            const index = parent.indexOf(value);

            // Validamos que la fecha actual sea posterior a la anterior
            if (index > 0) {
              const prevDate = new Date(parent[index - 1]);
              const currDate = new Date(value);
              if (currDate <= prevDate) {
                return this.createError({ path, message: 'La fecha debe ser posterior a la anterior.' });
              }
            }
            return true;
          })
      )
      .min(2, 'Debe haber al menos 2 sesiones.')
      .required('Debe agregar las sesiones.'),
    otherwise: Yup.array().optional(),
  }),

  coordenadas: Yup.object()
    .whenCM(
      Yup.object().shape({
        latitud: Yup.number().required(),
        longitud: Yup.number().required(),
      }),
      'idModalidad',
      [1, 3]
    )
    .label('Coordenadas del Evento'),

  /* // No se usan en front
  isPrivado: Yup.boolean().required(),
  idModoEvento: Yup.number().required(),
  TieneAsistenciaQR: Yup.boolean().required(),
  shortUrlAsistencia: Yup.boolean().required(),
  textoPreregistro: Yup.boolean().required(),
  LinkPreregistro: Yup.boolean().required(),
  */
});

export const EventosEditSchema = Yup.object({
  idEvento: Yup.number().required(),
  idEstatus: Yup.number().required(),

  imgURL: Yup.string().optional(),
  imgMovilURL: Yup.string().optional(),
  files: Yup.array().optional().label('Imagenes del evento'),

  idPartido: Yup.number().required(),
  idAmbito: Yup.number().required(),
  AmbitoValor: Yup.number().required(),

  idTipoActividad: Yup.number().required(),
  Actividad: Yup.string().min(4).required('Nombre del evento es requerido'),
  Descripcion: Yup.string().min(5).required(),
  FechaInicio: Yup.date().required().label('Fecha de inicio del evento'),
  FechaFin: Yup.date()
    .required()
    .label('Fecha de fin del evento')
    .when('FechaInicio', (startDate, schema) => {
      if (startDate instanceof Date) {
        return schema.min(startDate, 'La fecha de fin debe ser posterior a la fecha de inicio');
      }
      return schema;
    }),

  idPlantillaQR: Yup.number().required(),
  idModalidad: Yup.number().required(),
  NombreLugar: Yup.string().min(6).whenCM(Yup.string(), 'idModalidad', [1, 3]).label('Nombre del Lugar'),
  Calle: Yup.string().whenCM(Yup.string().min(2), 'idModalidad', [1, 3]),
  NumExt: Yup.string().whenCM(Yup.string().min(1), 'idModalidad', [1, 3]).label('Num Ext'),
  NumInt: Yup.string().optional().min(0).max(20).nullable().label('Num Int'),
  Colonia: Yup.string().whenCM(Yup.string().min(3), 'idModalidad', [1, 3]),
  idMunicipio: Yup.number().whenCM(Yup.number().positive('Elige un municipio'), 'idModalidad', [1, 3]),
  LigaVirtual: Yup.string()
    .whenCM(
      Yup.string()
        .matches(/^(ftp|http|https):\/\/[^ "]+$/, 'El link no es válido')
        .min(10),
      'idModalidad',
      [2, 3]
    )
    .label('Liga del Evento'),

  tienePreguntas: Yup.bool().required(),
  preguntas: Yup.array().when('tienePreguntas', {
    is: true,
    then: preguntasSchema(true),
    otherwise: Yup.array().optional(),
  }),

  TienePreregistro: Yup.number().required(),
  FechaFinPreregistro: Yup.date()
    .whenCM(
      Yup.date().max(
        Yup.ref('FechaInicio'),
        'La fecha de fin del preregistro no puede ser posterior a la fecha de inicio del evento'
      ),
      'TienePreregistro',
      1
    )
    .label('Fecha de fin preregistro del evento'),

  noSesiones: Yup.number().min(1).max(99).required().label('Numero de sesiones'),
  sesiones: Yup.array().when('noSesiones', {
    is: (noSesiones) => noSesiones && noSesiones > 1,
    then: Yup.array()
      .of(
        Yup.date()
          .required('La fecha es obligatoria.')
          .test('fecha-cronologica', 'La fecha debe ser posterior a la anterior.', function (value) {
            const { path, parent } = this;
            const index = parent.indexOf(value);

            // Validamos que la fecha actual sea posterior a la anterior
            if (index > 0) {
              const prevDate = new Date(parent[index - 1]);
              const currDate = new Date(value);
              if (currDate <= prevDate) {
                return this.createError({ path, message: 'La fecha debe ser posterior a la anterior.' });
              }
            }
            return true;
          })
      )
      .min(2, 'Debe haber al menos 2 sesiones.')
      .required('Debe agregar las sesiones.'),
    otherwise: Yup.array().optional(),
  }),

  coordenadas: Yup.object()
    .whenCM(
      Yup.object().shape({
        latitud: Yup.number().required(),
        longitud: Yup.number().required(),
      }),
      'idModalidad',
      [1, 3]
    )
    .label('Coordenadas del Evento'),
});

export const EventoPreguntasSchema = Yup.object({
  idEvento: Yup.number().required(),

  preguntas: preguntasSchema(true),
});

export const EventosFilterSchema = Yup.object({
  actividad: Yup.string().optional(),
});

export const EventosInvitadosFilterSchema = Yup.object({
  celular: Yup.string().optional(),
});

export const CompromisoEventSchema = (type = 0, idModoEvento = 2) =>
  Yup.object({
    idEvento: Yup.number().required(),
    idModoEvento: Yup.number().required(),

    account: Yup.object().when('idModoEvento', {
      is: 3,
      then: Yup.object().nullable().notRequired(),
      otherwise: Yup.object().shape({
        // Username: Yup.string().phone().min(10).max(10).required().label('Celular'),
        Password: Yup.string()
          .required()
          .min(8)
          .matches(
            /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$ %^&*-]).{8,}$/,
            'La contraseña debe contener una letra Mayúscula,Número y Carecter Especial'
          )
          .label('Contraseña'),
        Confirm: Yup.string()
          .oneOf([Yup.ref('Password'), null], 'Las contraseñas no  coinciden')
          .required()
          .label('Confirmar contraseña'),
        PIN: Yup.string().required().min(5).max(5).label('PIN'),
      }),
    }),

    user: Yup.object().shape({
      isMenor: Yup.number().required(),

      Nombre: Yup.string().required().min(3).label('Nombre'),
      Paterno: Yup.string().required().min(2).label('Apellido Paterno'),
      Materno: Yup.string().required().min(2).label('Apellido Materno'),
      FechaNacimiento: Yup.date()
        .required()
        .label('Fecha de nacimiento')
        .typeError('Ingrese una Clave de Elector válida'),
      idSexo: Yup.number().required().label('Sexo').typeError('Ingrese una Clave de Elector válida'),
      idEstadoNacimiento: Yup.number()
        .required()
        .label('Estado de nacimiento')
        .typeError('Ingrese una Clave de Elector válida'),

      INE: Yup.string().when('isMenor', {
        is: 0,
        then: Yup.string()
          .min(18)
          .max(18)
          .matches(patternINE, 'Ingrese una Clave de Elector válida')
          .required()
          .label('Clave de Elector'),
        otherwise: Yup.string().optional().nullable(),
      }),
      VigenciaINE: Yup.number()
        .optional()
        .nullable()
        .typeError('Ingrese una Vigencia INE válida')
        .label('Vigencia INE'),
      SeccionVota: Yup.number().when('isMenor', {
        is: 0,
        then: Yup.number().required().min(1).max(3900).label('Sección Vota'),
        otherwise: Yup.number().optional().nullable(),
      }),

      CURP: Yup.string().when('isMenor', {
        is: 1,
        then: Yup.string()
          .min(18)
          .max(18)
          .matches(patternCURP, 'Ingrese una CURP válida')
          .required()
          .label('CURP'),
        otherwise: Yup.string().optional().nullable(),
      }),
      // EsMilitante: '', // Cuando isBasic = true
    }),

    contact: Yup.object().shape({
      Celular: Yup.string()
        .phone()
        .min(10)
        .max(10)
        .required()
        .notOneOf(['9999999999'], 'Ingrese un número de teléfono válido')
        .label('Celular'),
      Telefono: Yup.string().phone().min(10).max(10).optional().nullable().label('Telefono'),
      TelMensajes: Yup.string().phone().min(10).max(10).optional().nullable().label('Telefono mensajes'),
      Correo: Yup.string().email().required().label('Correo Electronico'),
    }),

    address: Yup.object().shape({
      CalleVive: Yup.string().required().min(2).label('Calle donde vive'),
      NumExtVive: Yup.string().optional().nullable().label('No. Exterior'),
      NumIntVive: Yup.string().optional().nullable().label('No. Interior'),
      idEntidadVive: Yup.number().required().label('Estado'),
      idMunicipioVive: Yup.number().required().label('Municipio'),
      idLocalidadVive: Yup.number().required().label('Localidad'),
      ColoniaVive: Yup.string().required().min(3).label('Colonia'),
      CPVive: Yup.number()
        .required()
        .integer()
        .positive()
        .test('len', 'El código postal debe tener 5 dígitos', (val) => val && val.toString().length === 5)
        .label('El código postal'),
    }),

    social: Yup.object().shape({
      facebook: Yup.string().nullable().validUrl('Facebook'),
      instagram: Yup.string().nullable().validUrl('Instagram'),
      tiktok: Yup.string().nullable().validUrl('TikTok'),
      x: Yup.string().nullable().validUrl('X'),
    }),

    ...(idModoEvento !== 3
      ? {
          academy: Yup.object().shape({
            idGrade: Yup.number().required().min(1, 'Elige un grado').label('Último grado'),
            generalEducation: Yup.string().min(3).required().label('Formación General'),
          }),
        }
      : {}),

    ...(type === ID_FOCA && idModoEvento !== 3
      ? {
          employment: Yup.object().shape({
            idWork: Yup.number().min(0, 'Elige una ocupación').required().label('Trabajo actual'),
            occupation: Yup.string().whenCM(Yup.string().min(3), 'idWork', 0).label('Ocupación Actual'),
            workplace: Yup.string().min(3).optional().nullable().label('Lugar de trabajo'),
          }),

          formation: Yup.object().shape({
            foundBy: Yup.string().min(3).optional().nullable().label('Historia'),
            topics: Yup.array()
              .transform((value) => (Array.isArray(value) && value.length === 0 ? null : value))
              .optional()
              .nullable()
              .label('Temas'),
            days: Yup.array().min(2, 'Ingresa al menos 2 dias').required().label('Dias'),
            initialDate: Yup.date().required().label('Hora inicial'),
            finalDate: Yup.date().required().label('Hora final'),
            idParticipate: Yup.number().min(0).max(1).required().label('Trabajo actual'),
            participateProyect: Yup.string()
              .whenCM(Yup.string().min(3), 'idParticipate', 1)
              .label('Asociación o proyecto'),
            participateDescription: Yup.string()
              .whenCM(Yup.string().min(3).max(255), 'idParticipate', 1)
              .label('Descripción del cargo'),
          }),

          invitation: Yup.object().shape({
            idInvite: Yup.number().min(0).max(1).optional().nullable().label('Invitar'),
            idTrainer: Yup.number().min(0).max(1).required().label('Capacitar'),
            branch: Yup.string().whenCM(Yup.string().min(3), 'idTrainer', 1).label('Rama'),
          }),
        }
      : {}),

    extras: Yup.object().shape({
      idPartido: Yup.number().optional().nullable(),
      idUsuarioPropietario: Yup.number().optional().nullable(),
      idEstructuraSocial: Yup.number().optional().nullable(),
      medioConfirmacion: Yup.string().optional().nullable(),

      EsVoluntario: Yup.number().optional().nullable(),
      // idVoluntario: null,
      // TieneAuto: 0,
      // TieneExperiencia: 0,
      // AceptaCuidarOtra: 0,
    }),
    volunteer: Yup.object().shape({
      EsVoluntario: Yup.number()
        .nullable()
        .required('Indica si participas en alguna actividad')
        .label('Participa en actividad'),

      idVoluntario: Yup.number()
        .nullable()
        .when(['EsVoluntario', 'idDeseaParticipar'], {
          is: (esVoluntario, idDeseaParticipar) =>
            Number(esVoluntario) === 1 || Number(idDeseaParticipar) === 1,
          then: (schema) => schema.required('Selecciona en qué actividad participas').label('Actividad'),
          otherwise: (schema) => schema.nullable().optional(),
        }),

      idDeseaParticipar: Yup.number()
        .nullable()
        .when('EsVoluntario', {
          is: (val) => Number(val) === 0,
          then: (schema) =>
            schema.required('Indica si deseas participar en alguna actividad').label('Desea participar'),
          otherwise: (schema) => schema.nullable().optional(),
        }),
    }),
  });

export const CompromisoBasicEventExternoSchema = Yup.object({
  Nombre: Yup.string().required().min(3).label('Nombre'),
  Paterno: Yup.string().required().min(2).label('Apellido Paterno'),
  Materno: Yup.string().required().min(2).label('Apellido Materno'),

  idMunicipioVive: Yup.number().required().label('Municipio'),

  EsMilitante: Yup.number().min(0).max(1).required().label('Es militante'),

  Celular: Yup.string().phone().required().min(10).max(10),
  Telefono: Yup.string().phone().optional().nullable().min(10).max(10),
  TelMensajes: Yup.string().phone().optional().nullable().min(10).max(10),
});

export const PlantillaSchema = Yup.object().shape({
  Titulo: Yup.string().min(3).max(90).required('Titulo de la plantilla requerido').label('Titulo'),
  idTipo: Yup.number().oneOf([2, 3]).required('Tipo es requerido').label('Tipo'),
  files: Yup.array()
    .min(1)
    .max(1)
    .of(
      Yup.object().shape({
        file: Yup.string().required('Imagen es requerida'),
      })
    )
    .required('Debe incluir exactamente una imagen'),
});
